import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Toaster, toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  FileText,
  Building2,
  Settings,
  LogOut,
  Menu,
  Home,
  ChevronRight,
  Plus,
  Edit,
  Trash2,
  TrendingUp,
  DollarSign,
  UserCheck,
  Activity,
  ArrowLeft,
  BookOpen,
  MessageCircle,
  Smile
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AdminChatTab from '../components/AdminChatTab';
import AdminDentalChartTab from '../components/AdminDentalChartTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin Sidebar
const AdminSidebar = ({ isOpen, setIsOpen }) => {
  const { user, logout } = useAuth();

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsOpen(false)} />}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-primary text-white transform transition-transform duration-300 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-white/10">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <span className="text-primary font-bold text-lg">M</span>
              </div>
              <div>
                <span className="font-semibold text-sm">MED CONNECT</span>
                <span className="text-accent font-light ml-1 text-sm">ADMIN</span>
              </div>
            </Link>
          </div>

          <div className="p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <span className="font-semibold">{user?.name?.[0]?.toUpperCase() || 'A'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.name}</p>
                <p className="text-xs text-white/60 truncate">Administrator</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            <Link to="/admin" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium bg-white/10" onClick={() => setIsOpen(false)}>
              <LayoutDashboard className="w-5 h-5" />Dashboard
            </Link>
          </nav>

          <div className="p-4 border-t border-white/10 space-y-2">
            <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/70 hover:bg-white/10">
              <Home className="w-5 h-5" />User Dashboard
            </Link>
            <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-300 hover:bg-red-500/20 w-full">
              <LogOut className="w-5 h-5" />Sign Out
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Stats Overview
const StatsOverview = ({ stats }) => {
  const statCards = [
    { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'bg-blue-100 text-blue-600' },
    { label: 'Patients', value: stats?.total_patients || 0, icon: UserCheck, color: 'bg-green-100 text-green-600' },
    { label: 'Partners', value: stats?.total_partners || 0, icon: TrendingUp, color: 'bg-purple-100 text-purple-600' },
    { label: 'Pending Estimates', value: stats?.pending_estimates || 0, icon: FileText, color: 'bg-orange-100 text-orange-600' },
    { label: 'Total Referrals', value: stats?.total_referrals || 0, icon: Activity, color: 'bg-cyan-100 text-cyan-600' },
    { label: 'Active Businesses', value: stats?.total_businesses || 0, icon: Building2, color: 'bg-pink-100 text-pink-600' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {statCards.map((stat, index) => (
        <motion.div
          key={stat.label}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className="bg-white rounded-xl border border-slate-100 shadow-sm p-4"
        >
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${stat.color}`}>
            <stat.icon className="w-5 h-5" />
          </div>
          <p className="text-2xl font-bold text-primary">{stat.value}</p>
          <p className="text-xs text-muted-foreground">{stat.label}</p>
        </motion.div>
      ))}
    </div>
  );
};

// Users Tab
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/users`, { credentials: 'include' });
        if (response.ok) setUsers(await response.json());
      } catch (error) {
        console.error('Failed to fetch users:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const updateUserRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });
      if (response.ok) {
        toast.success('User role updated');
        setUsers(users.map(u => u.user_id === userId ? { ...u, role: newRole } : u));
      }
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  if (isLoading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Name</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Email</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Role</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Referral Code</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Joined</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((user) => (
              <tr key={user.user_id} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-sm font-medium">{user.name}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{user.email}</td>
                <td className="px-4 py-3">
                  <Badge className={user.role === 'admin' ? 'bg-red-100 text-red-700' : user.role === 'partner' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                    {user.role}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-sm font-mono">{user.referral_code}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{new Date(user.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <Select value={user.role} onValueChange={(v) => updateUserRole(user.user_id, v)}>
                    <SelectTrigger className="h-8 w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="patient">Patient</SelectItem>
                      <SelectItem value="partner">Partner</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Estimates Tab
const EstimatesTab = () => {
  const [estimates, setEstimates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [responseData, setResponseData] = useState({ status: '', admin_response: '', estimated_price: '' });

  useEffect(() => {
    fetchEstimates();
  }, []);

  const fetchEstimates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/estimates`, { credentials: 'include' });
      if (response.ok) setEstimates(await response.json());
    } catch (error) {
      console.error('Failed to fetch estimates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateEstimate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/estimates/${selectedEstimate.estimate_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(responseData)
      });
      if (response.ok) {
        toast.success('Estimate updated');
        fetchEstimates();
        setSelectedEstimate(null);
      }
    } catch (error) {
      toast.error('Failed to update estimate');
    }
  };

  if (isLoading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Treatment</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Description</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Price</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Date</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {estimates.map((est) => (
              <tr key={est.estimate_id} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-sm font-medium capitalize">{est.treatment_type.replace(/_/g, ' ')}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground truncate max-w-xs">{est.description}</td>
                <td className="px-4 py-3">
                  <Badge className={est.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : est.status === 'quoted' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                    {est.status}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-sm font-medium">{est.estimated_price ? `€${est.estimated_price}` : '-'}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{new Date(est.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" onClick={() => { setSelectedEstimate(est); setResponseData({ status: est.status, admin_response: est.admin_response || '', estimated_price: est.estimated_price || '' }); }}>
                        <Edit className="w-4 h-4 mr-1" />Respond
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Respond to Estimate</DialogTitle>
                        <DialogDescription>Provide pricing and response for this estimate request.</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label>Status</Label>
                          <Select value={responseData.status} onValueChange={(v) => setResponseData({...responseData, status: v})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="reviewed">Reviewed</SelectItem>
                              <SelectItem value="quoted">Quoted</SelectItem>
                              <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Estimated Price (€)</Label>
                          <Input value={responseData.estimated_price} onChange={(e) => setResponseData({...responseData, estimated_price: e.target.value})} placeholder="e.g., 500" />
                        </div>
                        <div>
                          <Label>Response Message</Label>
                          <Textarea value={responseData.admin_response} onChange={(e) => setResponseData({...responseData, admin_response: e.target.value})} rows={4} placeholder="Your response to the patient..." />
                        </div>
                        <Button onClick={updateEstimate} className="w-full">Update Estimate</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Referrals Tab
const ReferralsTab = () => {
  const [referrals, setReferrals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchReferrals = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/referrals`, { credentials: 'include' });
        if (response.ok) setReferrals(await response.json());
      } catch (error) {
        console.error('Failed to fetch referrals:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchReferrals();
  }, []);

  const updateReferral = async (referralId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/referrals/${referralId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        toast.success('Referral updated');
        setReferrals(referrals.map(r => r.referral_id === referralId ? { ...r, ...updates } : r));
      }
    } catch (error) {
      toast.error('Failed to update referral');
    }
  };

  if (isLoading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Referred User</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Email</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Rate</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Commission</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {referrals.map((ref) => (
              <tr key={ref.referral_id} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-sm font-medium">{ref.referred_user_name}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{ref.referred_user_email}</td>
                <td className="px-4 py-3">
                  <Badge className={ref.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : ref.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                    {ref.status}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-sm">{ref.commission_rate}%</td>
                <td className="px-4 py-3 text-sm font-medium">{ref.commission_amount ? `€${ref.commission_amount}` : '-'}</td>
                <td className="px-4 py-3">
                  <Select value={ref.status} onValueChange={(v) => updateReferral(ref.referral_id, { status: v })}>
                    <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Businesses Tab
const BusinessesTab = () => {
  const [businesses, setBusinesses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '', category: 'clinic', description: '', offerings: '', discount: '', contact_info: '', address: '', website: ''
  });

  useEffect(() => { fetchBusinesses(); }, []);

  const fetchBusinesses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/businesses`, { credentials: 'include' });
      if (response.ok) setBusinesses(await response.json());
    } catch (error) {
      console.error('Failed to fetch businesses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addBusiness = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/businesses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          offerings: formData.offerings.split(',').map(o => o.trim()).filter(o => o)
        })
      });
      if (response.ok) {
        toast.success('Business added');
        fetchBusinesses();
        setShowAddForm(false);
        setFormData({ name: '', category: 'clinic', description: '', offerings: '', discount: '', contact_info: '', address: '', website: '' });
      }
    } catch (error) {
      toast.error('Failed to add business');
    }
  };

  const deleteBusiness = async (id) => {
    if (!window.confirm('Are you sure you want to delete this business?')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/businesses/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Business deleted');
        fetchBusinesses();
      }
    } catch (error) {
      toast.error('Failed to delete business');
    }
  };

  if (isLoading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-primary">Partner Businesses</h3>
        <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="w-4 h-4 mr-2" />Add Business</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Business</DialogTitle>
              <DialogDescription>Add a new partner business to the network.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div><Label>Business Name</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} /></div>
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clinic">Clinic</SelectItem>
                    <SelectItem value="hotel">Hotel</SelectItem>
                    <SelectItem value="transport">Transport</SelectItem>
                    <SelectItem value="tourism">Tourism</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} /></div>
              <div><Label>Offerings (comma-separated)</Label><Input value={formData.offerings} onChange={(e) => setFormData({...formData, offerings: e.target.value})} placeholder="Service 1, Service 2" /></div>
              <div><Label>Discount for Members</Label><Input value={formData.discount} onChange={(e) => setFormData({...formData, discount: e.target.value})} placeholder="e.g., 10% off" /></div>
              <div><Label>Contact Info</Label><Input value={formData.contact_info} onChange={(e) => setFormData({...formData, contact_info: e.target.value})} /></div>
              <div><Label>Address</Label><Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} /></div>
              <div><Label>Website (optional)</Label><Input value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} /></div>
              <Button onClick={addBusiness} className="w-full">Add Business</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Name</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Category</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Discount</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {businesses.map((biz) => (
                <tr key={biz.business_id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm font-medium">{biz.name}</td>
                  <td className="px-4 py-3"><Badge className="capitalize">{biz.category}</Badge></td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{biz.discount || '-'}</td>
                  <td className="px-4 py-3"><Badge className={biz.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{biz.is_active ? 'Active' : 'Inactive'}</Badge></td>
                  <td className="px-4 py-3">
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => deleteBusiness(biz.business_id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// AI Estimates Tab
const AIEstimatesTab = () => {
  const [estimates, setEstimates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchEstimates = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/ai-estimates`, { credentials: 'include' });
        if (response.ok) setEstimates(await response.json());
      } catch (error) {
        console.error('Failed to fetch AI estimates:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchEstimates();
  }, []);

  const updateStatus = async (estimateId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/ai-estimates/${estimateId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success('Status updated');
        setEstimates(estimates.map(e => e.estimate_id === estimateId ? { ...e, status: newStatus } : e));
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      ai_generated: 'bg-purple-100 text-purple-700',
      submitted_for_review: 'bg-yellow-100 text-yellow-700',
      contacted: 'bg-blue-100 text-blue-700',
      converted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{status?.replace(/_/g, ' ')}</Badge>;
  };

  if (isLoading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="p-4 border-b border-slate-100">
        <h3 className="font-semibold text-primary">AI Dental Estimates</h3>
        <p className="text-sm text-muted-foreground">Review and manage AI-generated estimates</p>
      </div>
      {estimates.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground">No AI estimates yet</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Date</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Treatments</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Total</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {estimates.map((est) => (
                <tr key={est.estimate_id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm">{new Date(est.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-sm">
                    <div>{est.contact_name || 'Not submitted'}</div>
                    {est.contact_email && <div className="text-xs text-muted-foreground">{est.contact_email}</div>}
                    {est.contact_phone && <div className="text-xs text-muted-foreground">{est.contact_phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {est.detected_treatments?.slice(0, 2).map((t, i) => (
                      <div key={i} className="text-xs">{t.treatment} ({t.affected_teeth})</div>
                    ))}
                    {est.detected_treatments?.length > 2 && <div className="text-xs text-muted-foreground">+{est.detected_treatments.length - 2} more</div>}
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-accent">€{est.total_estimate}</td>
                  <td className="px-4 py-3">{getStatusBadge(est.status)}</td>
                  <td className="px-4 py-3">
                    <Select value={est.status} onValueChange={(v) => updateStatus(est.estimate_id, v)}>
                      <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ai_generated">AI Generated</SelectItem>
                        <SelectItem value="submitted_for_review">Submitted</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="converted">Converted</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Dental Pricing Tab
const DentalPricingTab = () => {
  const [pricing, setPricing] = useState({ treatments: [], confidence_threshold: 60, ai_enabled: true });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/dental-pricing`, { credentials: 'include' });
        if (response.ok) setPricing(await response.json());
      } catch (error) {
        console.error('Failed to fetch pricing:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPricing();
  }, []);

  const updatePrice = (index, newPrice) => {
    const updated = [...pricing.treatments];
    updated[index].price = parseFloat(newPrice) || 0;
    setPricing({ ...pricing, treatments: updated });
  };

  const toggleEnabled = (index) => {
    const updated = [...pricing.treatments];
    updated[index].enabled = !updated[index].enabled;
    setPricing({ ...pricing, treatments: updated });
  };

  const savePricing = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/dental-pricing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(pricing)
      });
      if (response.ok) toast.success('Dental pricing saved');
    } catch (error) {
      toast.error('Failed to save pricing');
    }
  };

  if (isLoading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>AI Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>AI Estimator Enabled</Label>
              <p className="text-sm text-muted-foreground">Turn the AI dental estimator on/off</p>
            </div>
            <input type="checkbox" checked={pricing.ai_enabled} onChange={(e) => setPricing({...pricing, ai_enabled: e.target.checked})} className="w-5 h-5" />
          </div>
          <div>
            <Label>Confidence Threshold (%)</Label>
            <p className="text-sm text-muted-foreground mb-2">Minimum confidence for auto-inclusion in estimate</p>
            <Input type="number" value={pricing.confidence_threshold} onChange={(e) => setPricing({...pricing, confidence_threshold: parseInt(e.target.value) || 60})} className="w-32" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Treatment Pricing (€)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {pricing.treatments?.map((treatment, index) => (
              <div key={treatment.treatment_id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                <input type="checkbox" checked={treatment.enabled} onChange={() => toggleEnabled(index)} className="w-4 h-4" />
                <span className={`flex-1 font-medium ${!treatment.enabled ? 'text-muted-foreground line-through' : ''}`}>{treatment.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">€</span>
                  <Input type="number" value={treatment.price} onChange={(e) => updatePrice(index, e.target.value)} className="w-24" disabled={!treatment.enabled} />
                </div>
              </div>
            ))}
          </div>
          <Button onClick={savePricing} className="w-full mt-4">Save Pricing</Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Commission Settings Tab
const CommissionTab = () => {
  const [settings, setSettings] = useState({ min_rate: 5, max_rate: 10, default_rate: 7.5 });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/commission-settings`, { credentials: 'include' });
        if (response.ok) setSettings(await response.json());
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const saveSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/commission-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });
      if (response.ok) toast.success('Commission settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  if (isLoading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <Card className="max-w-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5" />Commission Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Minimum Rate (%)</Label>
          <Input type="number" value={settings.min_rate} onChange={(e) => setSettings({...settings, min_rate: parseFloat(e.target.value)})} />
        </div>
        <div>
          <Label>Maximum Rate (%)</Label>
          <Input type="number" value={settings.max_rate} onChange={(e) => setSettings({...settings, max_rate: parseFloat(e.target.value)})} />
        </div>
        <div>
          <Label>Default Rate (%)</Label>
          <Input type="number" value={settings.default_rate} onChange={(e) => setSettings({...settings, default_rate: parseFloat(e.target.value)})} />
        </div>
        <Button onClick={saveSettings} className="w-full">Save Settings</Button>
      </CardContent>
    </Card>
  );
};

// Documentation Tab
const DocumentationTab = () => {
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetch('/documentation.md')
      .then(res => res.text())
      .then(text => { setContent(text); setIsLoading(false); })
      .catch(() => { setContent('# Documentation not found'); setIsLoading(false); });
  }, []);

  if (isLoading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div data-testid="documentation-tab" className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 md:p-8">
      <div className="prose prose-slate max-w-none
        prose-headings:text-primary prose-headings:font-semibold
        prose-h1:text-2xl prose-h1:border-b prose-h1:border-slate-200 prose-h1:pb-3 prose-h1:mb-6
        prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3
        prose-h3:text-lg prose-h3:mt-6
        prose-h4:text-base prose-h4:mt-4
        prose-p:text-sm prose-p:leading-relaxed prose-p:text-slate-600
        prose-a:text-accent prose-a:no-underline hover:prose-a:underline
        prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:text-slate-700 prose-code:font-mono
        prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-pre:rounded-lg prose-pre:p-4 prose-pre:text-xs prose-pre:overflow-x-auto
        prose-table:text-sm prose-th:bg-slate-50 prose-th:px-3 prose-th:py-2 prose-th:text-left prose-th:font-medium prose-th:text-primary
        prose-td:px-3 prose-td:py-2 prose-td:border-b prose-td:border-slate-100
        prose-li:text-sm prose-li:text-slate-600
        prose-strong:text-primary prose-strong:font-semibold
        prose-hr:border-slate-200
      ">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
      </div>
    </div>
  );
};

// Main Admin Panel
export default function AdminPanel() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }

    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/stats`, { credentials: 'include' });
        if (response.ok) setStats(await response.json());
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    fetchStats();
  }, [isAdmin, navigate]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-panel">
      <Toaster position="top-center" richColors />
      <AdminSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      <header className="sticky top-0 z-30 bg-white border-b border-slate-100 lg:pl-64">
        <div className="flex items-center justify-between px-4 md:px-8 h-16">
          <div className="flex items-center gap-4">
            <button className="lg:hidden p-2 -ml-2" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Admin</span>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">Dashboard</span>
            </div>
          </div>
        </div>
      </header>
      
      <main className="lg:pl-64 pt-6 pb-12 px-4 md:px-8">
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-semibold text-primary">Admin Dashboard</h1>
            <p className="text-muted-foreground">Manage users, estimates, referrals, and businesses.</p>
          </div>

          <StatsOverview stats={stats} />

          <Tabs defaultValue="users" className="space-y-6">
            <TabsList className="bg-slate-100 p-1 flex-wrap">
              <TabsTrigger value="users" className="data-[state=active]:bg-white">Users</TabsTrigger>
              <TabsTrigger value="ai-estimates" className="data-[state=active]:bg-white">AI Estimates</TabsTrigger>
              <TabsTrigger value="estimates" className="data-[state=active]:bg-white">Estimates</TabsTrigger>
              <TabsTrigger value="referrals" className="data-[state=active]:bg-white">Referrals</TabsTrigger>
              <TabsTrigger value="businesses" className="data-[state=active]:bg-white">Businesses</TabsTrigger>
              <TabsTrigger value="dental-chart" data-testid="dental-chart-tab-trigger" className="data-[state=active]:bg-white"><Smile className="w-4 h-4 mr-1.5" />Dental Chart</TabsTrigger>
              <TabsTrigger value="live-chat" data-testid="live-chat-tab-trigger" className="data-[state=active]:bg-white"><MessageCircle className="w-4 h-4 mr-1.5" />Live Chat</TabsTrigger>
              <TabsTrigger value="dental-pricing" className="data-[state=active]:bg-white">Dental Pricing</TabsTrigger>
              <TabsTrigger value="commission" className="data-[state=active]:bg-white">Commission</TabsTrigger>
              <TabsTrigger value="documentation" data-testid="documentation-tab-trigger" className="data-[state=active]:bg-white"><BookOpen className="w-4 h-4 mr-1.5" />Docs</TabsTrigger>
            </TabsList>

            <TabsContent value="users"><UsersTab /></TabsContent>
            <TabsContent value="ai-estimates"><AIEstimatesTab /></TabsContent>
            <TabsContent value="estimates"><EstimatesTab /></TabsContent>
            <TabsContent value="referrals"><ReferralsTab /></TabsContent>
            <TabsContent value="businesses"><BusinessesTab /></TabsContent>
            <TabsContent value="dental-chart"><AdminDentalChartTab /></TabsContent>
            <TabsContent value="live-chat"><AdminChatTab /></TabsContent>
            <TabsContent value="dental-pricing"><DentalPricingTab /></TabsContent>
            <TabsContent value="commission"><CommissionTab /></TabsContent>
            <TabsContent value="documentation"><DocumentationTab /></TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
